/**
 * Where an OAuth round trip should put the user back.
 *
 * Pure and import-free on purpose, so it unit-tests in isolation (see
 * `tests/unit/oauthRedirect.test.ts`) — the same reason `guestConvert.ts` is.
 *
 * This exists because the rule it encodes was previously one line inside
 * `loginWithProvider`, and that line was wrong in a way review could not see:
 *
 *     localStorage.setItem('auth_redirect', window.location.pathname);
 *
 * It overwrote whatever the caller had stored immediately beforehand, and
 * dropped the query string doing it. Callers looked correct — `AdminOnboarding`
 * had a whole `oauthReturnTo()` function deciding the address one line above —
 * and the damage happened in a callee nobody had reason to open. A guest
 * conversion left for `/register?convert=true&guestId=…`, came back to a bare
 * `/register`, was re-read as a plain signup, and the fan was forwarded to the
 * workspace builder with their room session abandoned.
 */

export interface OAuthRedirectInput {
  /** What the caller explicitly asked for. Always wins when present. */
  requested?: string | null;
  /**
   * Whatever is already parked in `auth_redirect` — some surfaces stage a
   * destination from `?redirect=` in an effect, before any button is pressed.
   */
  stored?: string | null;
  /** Current path. */
  pathname: string;
  /** Current query string, INCLUDING the leading `?`. */
  search?: string | null;
}

/**
 * Precedence: what the caller asked for → what was already staged → where we
 * are now.
 *
 * The last of those keeps the query string. An entry context lives entirely in
 * the query (`?convert=true&guestId=…`, `?fan=1`, `?eventToken=…`), so a
 * pathname-only fallback silently downgrades every one of them to a plain
 * signup — which is the whole bug this module is named after.
 */
export function resolveOAuthRedirect(input: OAuthRedirectInput): string {
  // Both candidates pass through `safeSignInRedirect`: `OAuthCallback` follows
  // whatever is parked here, and `stored` in particular may have been written
  // by any page. An unsafe value is skipped, never followed (issue #815).
  const requested = safeSignInRedirect(nonEmpty(input.requested));
  if (requested) return requested;

  const stored = safeSignInRedirect(nonEmpty(input.stored));
  if (stored) return stored;

  return `${input.pathname}${input.search ?? ''}`;
}

function nonEmpty(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

const MAX_REDIRECT_LENGTH = 4096;
// Only ever used to resolve against; never navigated to.
const PLACEHOLDER_ORIGIN = 'https://showliner.invalid';
// Whitespace (the URL parser strips it, and drops tabs and newlines from the
// MIDDLE of a URL, so `/<tab>/evil.example` parses as `//evil.example`), C0
// controls, DEL, and backslashes (browsers read `/\host` as `//host`).
// biome-ignore lint/suspicious/noControlCharactersInRegex: matching control characters is the point
const UNSAFE_REDIRECT_CHARS = /[\s\u0000-\u001f\u007f\\]/;
// How many layers of percent-encoding the path check peels off. A router or a
// server that decodes once more than we did must still find nothing.
const MAX_DECODE_PASSES = 3;

/**
 * The one same-origin path rule (issue #815): may a value read from a query
 * string or `localStorage` be followed as a post-sign-in destination ON THIS
 * ORIGIN?
 *
 * Accepted: a path starting with exactly one `/`, with its query and fragment.
 * Returned normalized (as the URL parser resolves it against this origin), so
 * what was checked is what gets navigated to.
 *
 * Refused, returning null: protocol-relative URLs (`//host`), backslash forms
 * (`/\host`, `\\host`), the same shapes percent-encoded in the path
 * (`/%2F%2Fhost`, `/%5Chost`, double-encoded too), any scheme (`javascript:`,
 * `data:`, `https://other`), relative paths with no leading slash, whitespace
 * and control characters anywhere (the URL parser silently strips them, which
 * is how `<tab>//host` becomes `//host`), and anything over 4096 characters.
 *
 * The query is not decoded: `/oauth/consent?redirect_uri=https%3A%2F%2F…`
 * legitimately carries an encoded URL, and it is a value, never a location.
 */
export function safeSameOriginPath(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  if (raw.length === 0 || raw.length > MAX_REDIRECT_LENGTH) return null;
  if (UNSAFE_REDIRECT_CHARS.test(raw)) return null;
  if (raw.charAt(0) !== '/' || raw.charAt(1) === '/') return null;

  // The path alone, decoded as many times as anything downstream might.
  let path = raw.split(/[?#]/, 1)[0];
  for (let pass = 0; pass < MAX_DECODE_PASSES; pass += 1) {
    let decoded: string;
    try {
      decoded = decodeURIComponent(path);
    } catch {
      return null;
    }
    if (UNSAFE_REDIRECT_CHARS.test(decoded)) return null;
    if (decoded.startsWith('//')) return null;
    if (decoded === path) break;
    path = decoded;
  }

  let url: URL;
  try {
    url = new URL(raw, PLACEHOLDER_ORIGIN);
  } catch {
    return null;
  }
  if (url.origin !== PLACEHOLDER_ORIGIN) return null;
  const normalized = `${url.pathname}${url.search}${url.hash}`;
  // Belt and braces: the parser's own output must still be a single-slash path.
  if (!normalized.startsWith('/') || normalized.startsWith('//')) return null;
  return normalized;
}

function isLocalDevHost(hostname: string): boolean {
  return (
    hostname === 'localhost' || hostname.endsWith('.localhost') || hostname === '127.0.0.1' || hostname === '[::1]'
  );
}

/**
 * Hosts a sign-in may hand the user off to on ANOTHER origin: the Showliner
 * apps themselves (a fan returned to the live app, a visitor returned to the
 * marketing site) and a developer's machine. Nothing else.
 */
function isShowlinerHost(hostname: string): boolean {
  if (hostname === 'showliner.io' || hostname.endsWith('.showliner.io')) return true;
  return isLocalDevHost(hostname);
}

/**
 * A post-sign-in destination: a same-origin path (`safeSameOriginPath`), or an
 * absolute URL on a Showliner app. Returns the value to follow, or null.
 *
 * The absolute branch exists because two flows really do cross origins: a fan
 * who signed up on the admin origin goes back to the live app, and the
 * marketing site's `/login` stub forwards to admin with an absolute return
 * address. It is `https:` only (`http:` just for a local dev host), on
 * showliner.io or a subdomain, with no userinfo. Before #815 any scheme passed
 * once the host matched, so `javascript://showliner.io/%0Aalert(1)` reached
 * `window.location.href`.
 *
 * A caller tells the two apart by the leading `/`: a path goes through the
 * router, anything else is a full-page navigation.
 */
export function safeSignInRedirect(raw: unknown): string | null {
  const path = safeSameOriginPath(raw);
  if (path) return path;
  if (typeof raw !== 'string') return null;
  if (raw.length === 0 || raw.length > MAX_REDIRECT_LENGTH) return null;
  if (UNSAFE_REDIRECT_CHARS.test(raw)) return null;
  // Only an absolute http(s) URL gets this far; a relative value that failed
  // the path rule is refused rather than resolved against anything.
  if (!/^https?:\/\//i.test(raw)) return null;

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.username || url.password) return null;
  const local = isLocalDevHost(url.hostname);
  if (url.protocol !== 'https:' && !(url.protocol === 'http:' && local)) return null;
  if (!isShowlinerHost(url.hostname)) return null;
  // The value as written: it has passed every check above, and returning it
  // untouched keeps the address the caller built (no added trailing slash).
  return raw;
}

/** Where the Showliner app's sign-in resumes (`OAuthContinuePage`). */
export const APP_RETURN_PATH = '/oauth/continue';

/**
 * The one place a sign-up may be sent back to after the account step: the
 * Showliner app's continuation page (ADR-0010).
 *
 * The app signs in through `/oauth/authorize` → `/oauth/continue`, which
 * bounces a signed-out user to `/login?redirect=…`. A new user taps "Create
 * one" there, and the address has to survive the trip through `/register` or
 * they finish signing up on the workspace builder while the app waits forever
 * for a code (issue #783).
 *
 * That address arrives in a query string anyone can write, and it is handed to
 * the router after an account has just been created, so it is an open-redirect
 * candidate by construction. Hence an ALLOWLIST of one path rather than a
 * general "is this relative?" test: only `/oauth/continue`, on this origin,
 * with its query and nothing else. Everything the page itself needs (client,
 * PKCE, redirect URI) is in that query and is re-checked by the server, which
 * is the authority; this only decides where the browser goes.
 *
 * Returns the normalized path + query, or null for anything else. Refused:
 * absolute and protocol-relative URLs (`https://…`, `//host`), backslashes
 * (browsers read `/\host` as `//host`), whitespace and control characters,
 * fragments, dot segments that resolve elsewhere, and look-alike paths
 * (`/oauth/continuex`, `/oauth/continue/…`, `/oauth/consent`).
 */
export function appReturnPath(raw: unknown): string | null {
  // The general same-origin rule first, so the two can never disagree about
  // what leaves the origin; this only narrows it to one path.
  const path = safeSameOriginPath(raw);
  if (!path || typeof raw !== 'string') return null;
  // No fragment: nothing on the continuation page reads one, and a prefix
  // check is easiest to fool with something trailing it.
  if (raw.includes('#')) return null;
  if (!raw.startsWith(APP_RETURN_PATH)) return null;
  const next = raw.charAt(APP_RETURN_PATH.length);
  if (next !== '' && next !== '?') return null;

  const query = path.indexOf('?');
  const pathname = query === -1 ? path : path.slice(0, query);
  if (pathname !== APP_RETURN_PATH) return null;
  return path;
}
